import React, { type FC } from 'react';
import { CheckCircle2, ExternalLink } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { CardShell } from './card-shell';
import { ApprovalBanner } from './approval-banner';
import type { ToolCardProps } from './types';
import { safeGet, safeArray, formatDate } from './types';
import { trackCatalogRedirect } from '../../../lib/jobs-api';

export const ApplicationConfirmCard: FC<ToolCardProps> = ({
  args,
  result,
  stage,
  toolName,
  approvalId,
}) => {
  const { t } = useTranslation('seeker');
  const jobTitle = safeGet<string>(result, 'job', 'title');
  const companyName = safeGet<string>(result, 'company', 'name');
  const companyLocation = safeGet<string>(result, 'company', 'location');
  const outcome = safeGet<string>(result, 'outcome');
  const redirectUrl = safeGet<string>(result, 'redirectUrl');
  const catalogJobId =
    safeGet<string>(args, 'catalogJobId') ||
    safeGet<string>(result, 'application', 'catalogJobId') ||
    safeGet<string>(result, 'application', 'job', 'id');
  const statusHistory = safeArray<Record<string, unknown>>(result as Record<string, unknown>, 'statusHistory');
  const firstStatus = statusHistory[0];
  const statusDate = firstStatus ? formatDate(safeGet(firstStatus, 'at')) : '';

  const openExternalApply = () => {
    if (!redirectUrl) return;
    const opened =
      typeof window !== 'undefined'
        ? window.open(redirectUrl, '_blank', 'noopener,noreferrer')
        : null;
    if (catalogJobId) {
      void trackCatalogRedirect(catalogJobId, { redirectUrl }).catch(() => {});
    }
    if (!opened && typeof window !== 'undefined') {
      window.location.assign(redirectUrl);
    }
  };

  if (stage === 'pending_approval') {
    return (
      <CardShell>
        <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div
            style={{
              fontSize: 15,
              fontWeight: 600,
              color: 'var(--text-primary)',
            }}
          >
            Submit application?
          </div>
          <div
            style={{
              fontSize: 14,
              color: 'var(--text-secondary)',
            }}
          >
            {jobTitle}
            {companyName ? ` at ${companyName}` : ''}
          </div>
        </div>
        {approvalId && (
          <ApprovalBanner
            approvalId={approvalId}
            toolName={toolName}
            description="Confirm submitting this application?"
          />
        )}
      </CardShell>
    );
  }

  if (outcome === 'redirect_required' && redirectUrl) {
    return (
      <CardShell>
        <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <ExternalLink size={16} color="var(--text-secondary)" style={{ flexShrink: 0 }} />
            <span
              style={{
                fontSize: 14,
                fontWeight: 600,
                color: 'var(--text-primary)',
              }}
            >
              {t('jobs.externalApply')}
            </span>
          </div>
          <div
            style={{
              fontSize: 13,
              color: 'var(--text-secondary)',
            }}
          >
            {t('jobs.externalApplyHint')}
          </div>
          <button
            type="button"
            onClick={openExternalApply}
            style={{
              border: '1px solid var(--border-subtle)',
              borderRadius: 999,
              padding: '8px 12px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              alignSelf: 'flex-start',
              fontSize: 13,
              fontWeight: 600,
              color: 'var(--text-primary)',
              background: 'var(--surface-elevated)',
              cursor: 'pointer',
            }}
          >
            <ExternalLink size={14} />
            {t('jobs.applyOnWebsite')}
          </button>
        </div>
      </CardShell>
    );
  }

  // completed state
  return (
    <CardShell>
      <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <CheckCircle2 size={16} color="var(--text-secondary)" style={{ flexShrink: 0 }} />
          <span
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: 'var(--text-primary)',
            }}
          >
            Application submitted
          </span>
        </div>

        {/* Job title */}
        {jobTitle && (
          <div
            style={{
              fontSize: 15,
              fontWeight: 600,
              color: 'var(--text-primary)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {jobTitle}
          </div>
        )}

        {/* Company + location */}
        {(companyName || companyLocation) && (
          <div
            style={{
              fontSize: 13,
              color: 'var(--text-tertiary)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {companyName}
            {companyName && companyLocation && ' \u00B7 '}
            {companyLocation}
          </div>
        )}

        {/* Status badge + date */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: 'var(--text-secondary)',
              background: 'rgba(0,0,0,0.06)',
              borderRadius: 9999,
              padding: '3px 10px',
              lineHeight: '16px',
            }}
          >
            Applied
          </span>
          {statusDate && (
            <span
              style={{
                fontSize: 12,
                color: 'var(--text-tertiary)',
              }}
            >
              {statusDate}
            </span>
          )}
        </div>
      </div>
    </CardShell>
  );
};
